import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "../hooks/useMediaQuery.js";
import { useVisualViewport } from "../hooks/useVisualViewport.js";
import { Icon } from "../Icon.js";
import { Popover } from "../Popover.js";
import { useTexts } from "../texts.js";
import { IconButton, Kbd } from "../ui.js";
import { cx } from "../util.js";
import { Conversation } from "./Conversation.js";
import { AssistantAvatar } from "./MessageBubble.js";
import { SessionList } from "./SessionList.js";
const MIN_W = 360;
const DEFAULT_W = 440;
const WIDE_W = 760;
const maxWidth = () => Math.max(MIN_W, Math.min(1200, window.innerWidth - 140));
const clampWidth = (w) => Math.round(Math.min(maxWidth(), Math.max(MIN_W, w)));
function useDrawerWidth(key) {
    const [width, setWidthState] = useState(() => {
        let stored = 0;
        try {
            stored = Number(localStorage.getItem(key));
        }
        catch {
            /* private mode */
        }
        return clampWidth(stored || DEFAULT_W);
    });
    const setWidth = useCallback((w, persist = true) => {
        const v = clampWidth(w);
        setWidthState(v);
        if (persist) {
            try {
                localStorage.setItem(key, String(v));
            }
            catch {
                // private mode: the width is kept for this session only
            }
        }
    }, [key]);
    useEffect(() => {
        const onResize = () => setWidthState((w) => clampWidth(w));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return [width, setWidth];
}
/**
 * The assistant as a drawer on the right: drag the left edge (or arrow
 * keys) to resize, double click or the size button switches wide/narrow,
 * history in a popover, Esc inside closes it.
 */
export function AssistantDrawer({ cp, open, onClose, contextLabel, onOpenPage, headerExtra, widthKey = "enlixy.assistant.width", top = 76, tourPrefix = "assistant", composerRef, conversation, avatar, toolbar, body, }) {
    const t = useTexts();
    const own = useRef(null);
    const composer = composerRef ?? own;
    const [width, setWidth] = useDrawerWidth(widthKey);
    const [dragging, setDragging] = useState(false);
    const sheet = !useMediaQuery("(min-width: 1024px)");
    const vv = useVisualViewport(open && sheet);
    useEffect(() => {
        if (open)
            requestAnimationFrame(() => composer.current?.focus());
    }, [open, composer]);
    const startDrag = (e) => {
        if (e.button !== 0)
            return;
        e.preventDefault();
        const startX = e.clientX;
        const startW = width;
        let last = startW;
        setDragging(true);
        const prevSelect = document.body.style.userSelect;
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
        const move = (ev) => {
            last = startW + (startX - ev.clientX);
            setWidth(last, false);
        };
        const up = () => {
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
            document.body.style.userSelect = prevSelect;
            document.body.style.cursor = "";
            setDragging(false);
            setWidth(last);
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
    };
    if (!open)
        return null;
    const wide = width >= WIDE_W - 20;
    // Phones and tablets in portrait (below 1024 px): a full-screen sheet that
    // takes the visual viewport, so the composer stays above the on-screen
    // keyboard, with the safe-area insets of notches; from 1024 px (tablets in
    // landscape, desktops) the resizable side panel.
    const sheetStyle = vv ? { height: vv.height, top: vv.offsetTop } : { height: "100dvh", top: 0 };
    return (_jsxs("aside", { "aria-label": t.assistantName, "data-tour": `${tourPrefix}-drawer`, onKeyDown: (e) => {
            if (e.key === "Escape" && !e.defaultPrevented)
                onClose();
        }, className: cx(sheet
            ? "anim-sheet-in fixed inset-x-0 z-50 flex flex-col overflow-hidden bg-surface pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]"
            : "anim-slide-in fixed right-3 bottom-3 z-40 flex max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-[20px] bg-surface shadow-2xl ring-1 ring-line/70", dragging && "ring-2 ring-accent/60"), style: sheet ? sheetStyle : { width, top }, "aria-modal": sheet || undefined, role: sheet ? "dialog" : undefined, children: [!sheet && _jsx("div", { role: "separator", "aria-orientation": "vertical", "aria-label": t.assistantResize, "aria-valuemin": MIN_W, "aria-valuemax": maxWidth(), "aria-valuenow": width, tabIndex: 0, title: t.assistantResizeTitle, onPointerDown: startDrag, onDoubleClick: () => setWidth(wide ? DEFAULT_W : WIDE_W), onKeyDown: (e) => {
                    if (e.key === "ArrowLeft") {
                        e.preventDefault();
                        setWidth(width + 40);
                    }
                    else if (e.key === "ArrowRight") {
                        e.preventDefault();
                        setWidth(width - 40);
                    }
                    else if (e.key === "Home") {
                        e.preventDefault();
                        setWidth(MIN_W);
                    }
                    else if (e.key === "End") {
                        e.preventDefault();
                        setWidth(maxWidth());
                    }
                }, className: "group absolute inset-y-0 left-0 z-10 flex w-3 cursor-col-resize items-center justify-center outline-none", children: _jsx("span", { className: cx("h-12 w-1 rounded-full transition-colors", dragging ? "bg-accent" : "bg-line/80 group-hover:bg-accent/70 group-focus-visible:bg-accent") }) }), _jsxs("header", { className: "flex items-center gap-1.5 border-b border-line/70 py-2.5 pr-2 pl-4", children: [avatar ?? _jsx(AssistantAvatar, {}), _jsxs("div", { className: "ml-1 min-w-0 flex-1", children: [_jsx("h2", { className: "truncate text-sm font-semibold text-fg", children: cp.current?.title || t.assistantName }), contextLabel && _jsx("p", { className: cx("truncate text-[11px] text-muted", sheet && "hidden sm:block"), children: contextLabel })] }), _jsx("span", { "data-tour": `${tourPrefix}-history`, children: _jsx(Popover, { label: t.assistantEarlier, width: sheet ? 320 : Math.min(360, width - 40), triggerClassName: "flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-fg-3 hover:bg-hover hover:text-fg", trigger: () => (_jsxs(_Fragment, { children: [_jsx(Icon, { name: "history", size: 16 }), !sheet && width >= 480 && _jsx("span", { children: t.assistantHistory })] })), children: (close) => (_jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between border-b border-line/70 px-4 py-2.5", children: [_jsx("h3", { className: "text-[10px] font-semibold tracking-[0.2em] text-fg-2 uppercase", children: t.assistantConversations }), onOpenPage && (_jsx("button", { type: "button", className: "text-xs font-medium text-accent hover:underline dark:text-accent-on-dark", onClick: () => {
                                                    close();
                                                    onOpenPage();
                                                }, children: t.assistantAllOnPage }))] }), _jsx("div", { className: "scroll-thin max-h-[55vh] overflow-y-auto p-1.5", children: _jsx(SessionList, { sessions: cp.sessions, currentId: cp.sessionId, disabled: cp.streaming, limit: 30, onSelect: (id) => {
                                                cp.selectSession(id);
                                                close();
                                            }, onRename: cp.renameSession, onRemove: cp.removeSession }) })] })) }) }), headerExtra, !sheet && (_jsx(IconButton, { icon: wide ? "narrow" : "widen", label: wide ? t.assistantNarrower : t.assistantWider, onClick: () => setWidth(wide ? DEFAULT_W : WIDE_W) })), onOpenPage && !sheet && _jsx(IconButton, { icon: "maximize", label: t.assistantOpenPage, onClick: onOpenPage, disabled: cp.streaming }), _jsx(IconButton, { icon: "plus", label: t.assistantNewConversation, onClick: cp.newChat, disabled: cp.streaming }), _jsx(IconButton, { icon: "x", label: t.assistantClose, onClick: onClose })] }), toolbar && _jsx("div", { className: "border-b border-line/70 px-3 py-1.5 sm:py-2", children: toolbar }), body ?? _jsx(Conversation, { hint: _jsxs(_Fragment, { children: [_jsx(Kbd, { children: "Enter" }), " ", t.assistantKeySend, " \u00B7 ", _jsx(Kbd, { children: "\u21E7 Enter" }), " ", t.assistantKeyNewline, " \u00B7 ", _jsx(Kbd, { children: "Esc" }), " ", t.assistantKeyClose] }), ...conversation, cp: cp, composerRef: composer, wide: !sheet && width >= 640, onShowAll: onOpenPage, tourPrefix: tourPrefix })] }));
}
